import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { get } from "src/utils/networkUtils";

/**
 * Custom hook to fetch tenant competitions
 * @param {number|null} defaultTenantId - Default tenant ID to use if not in URL params
 * @returns {Object} { tenantCompetitions, loading, error, refetch }
 */
export default function useTenantCompetitions(defaultTenantId = 1) {
    const [tenantCompetitions, setTenantCompetitions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchParams] = useSearchParams();
    const previousTenantIdRef = useRef(null);

    // Get tenantId from URL params or use default
    const tenantId = searchParams.get("tenantId") || defaultTenantId;

    const fetchTenantCompetitions = useCallback(async () => {
        if (!tenantId) return;

        if (previousTenantIdRef.current === tenantId) {
            return;
        }

        previousTenantIdRef.current = tenantId;
        setLoading(true);
        setError(null);

        try {
            const response = await get(`/admin/tenants/${tenantId}/tenant-competitions`);
            const tenantCompetitionsData = response.tenantCompetitions || [];
            setTenantCompetitions(tenantCompetitionsData);
        } catch (err) {
            console.error("Error fetching tenant competitions:", err);
            setError(err);
            setTenantCompetitions([]);
        } finally {
            setLoading(false);
        }
    }, [tenantId]);

    // Fetch tenant competitions when tenantId changes
    useEffect(() => {
        fetchTenantCompetitions();
    }, [fetchTenantCompetitions]);

    return {
        tenantCompetitions,
        loading,
        error,
        refetch: fetchTenantCompetitions,
        tenantId
    };
}